// @ts-check
// ====== Lazy views loader (S1) ======
// Permette di estrarre interi <section class="view"> da index.html in file
// HTML separati sotto `/views/<id>.html`, caricati on-demand al primo accesso.
//
// Funziona così:
//   1. Al boot, lascia in index.html SOLO i nodi <section id="view-..."> vuoti
//      con attributo `data-lazy-view="<id>"`.
//   2. Quando il router fa setView('clienti'), chiama ensureViewLoaded('clienti').
//   3. La prima volta, fetch('/views/clienti.html'), inietta il markup nel <section>,
//      esegue le `<script>` inline che eventualmente contiene, e marca come caricato.
//
// Le views statiche già presenti in index.html (dashboard, todos, …) restano
// inline: il loader le ignora se non hanno `data-lazy-view`.
//
// NB: il pattern è opt-in per ridurre il rischio del refactor. L'estrazione
// progressiva può procedere una vista alla volta senza rompere quelle ancora
// inline.

const _loaded = new Set();
const _inflight = new Map(); // id -> Promise

/** @param {string} viewId */
async function ensureViewLoaded(viewId) {
  if (_loaded.has(viewId)) return true;
  if (_inflight.has(viewId)) return _inflight.get(viewId);

  // Selettore: container con data-lazy-view oppure section#view-<id>
  const container = /** @type {HTMLElement | null} */ (
    document.querySelector(`[data-lazy-view="${viewId}"], #view-${viewId}[data-lazy-view]`)
  );
  if (!container) {
    // Vista inline (non lazy): segna come caricata e prosegui
    _loaded.add(viewId);
    return true;
  }
  if (container.dataset.lazyLoaded === '1') {
    _loaded.add(viewId);
    return true;
  }

  const url = container.dataset.lazyUrl || `/views/${viewId}.html`;
  const promise = (async () => {
    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status} su ${url}`);
      const html = await res.text();
      container.innerHTML = html;
      // Esegui eventuali <script> inline contenuti nel fragment
      const scripts = container.querySelectorAll('script');
      scripts.forEach((old) => {
        const fresh = document.createElement('script');
        for (const attr of old.attributes) fresh.setAttribute(attr.name, attr.value);
        fresh.textContent = old.textContent || '';
        old.replaceWith(fresh);
      });
      container.dataset.lazyLoaded = '1';
      _loaded.add(viewId);
      // @ts-ignore — bus globale (event-bus.js)
      globalThis.bus?.emit('view:lazy-loaded', { viewId });
      return true;
    } catch (e) {
      console.error('[views-loader] failed', viewId, e);
      container.innerHTML = `<div class="card" style="padding:20px;color:var(--danger)">
        Impossibile caricare la vista <strong>${viewId}</strong>.<br/>
        <small>${(e && e.message) || e}</small></div>`;
      throw e;
    } finally {
      _inflight.delete(viewId);
    }
  })();
  _inflight.set(viewId, promise);
  return promise;
}

/** Preloading opportunistico in idle: scarica le view più probabili dopo il boot */
function preloadLikelyViews() {
  const likely = ['clienti', 'azioni', 'todos'];
  const start = () =>
    likely.forEach((v) =>
      ensureViewLoaded(v).catch(() => {
        /* best-effort */
      })
    );
  // @ts-ignore — requestIdleCallback non sempre tipizzato
  if (typeof requestIdleCallback === 'function') requestIdleCallback(start, { timeout: 3000 });
  else setTimeout(start, 2000);
}

// @ts-ignore — esposizione globale
globalThis.ensureViewLoaded = ensureViewLoaded;
// @ts-ignore
globalThis.preloadLikelyViews = preloadLikelyViews;

export { ensureViewLoaded, preloadLikelyViews };
